import appConfig from '@/features/app/constants/app-config';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * Get the client timezone from config or use the browser's timezone
 */
export function getClientTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get the server timezone from config
 */
export function getServerTimezone(): string {
  return appConfig.serverTimezone;
}

/**
 * Parse a date and convert it to client timezone for display
 * @param date - Unix timestamp (seconds), ISO string, or Dayjs object
 * @param fromTimezone - Source timezone (defaults to server timezone)
 * @returns Dayjs object in client timezone
 */
export function _clientTime(
  date: number | string | Date | Dayjs,
  fromTimezone: string = getServerTimezone()
): Dayjs {
  const clientTz = getClientTimezone();
  if (typeof date === 'number') return dayjs.unix(date).tz(clientTz);
  if (dayjs.isDayjs(date)) return date.tz(clientTz);
  return dayjs.tz(date, fromTimezone).tz(clientTz);
}

/**
 * Convert a date from client timezone to server timezone for sending to API
 * @param date - Date in client timezone
 * @param format - Optional format string (if provided, returns formatted string)
 * @returns Dayjs object in server timezone or formatted string
 */
export function _serverTime(date: Dayjs | Date | string): Dayjs;
export function _serverTime(date: Dayjs | Date | string, format: string): string;
export function _serverTime(date: Dayjs | Date | string, format?: string): Dayjs | string {
  const serverTz = getServerTimezone();
  const clientTz = getClientTimezone();
  let dayjsDate: Dayjs;

  if (dayjs.isDayjs(date)) {
    dayjsDate = date;
  } else if (typeof date === 'string') {
    dayjsDate = dayjs.tz(date, clientTz);
  } else {
    dayjsDate = dayjs(date);
  }

  const serverDate = dayjsDate.tz(serverTz);
  return format ? serverDate.format(format) : serverDate;
}

/**
 * Convert a date to Unix timestamp (always UTC)
 * Works with dates in any timezone
 * @param date - Date to convert
 * @returns Unix timestamp in seconds
 */
export function _unixTimestamp(date: Dayjs | Date | string | number): number {
  if (typeof date === 'number') return date;
  if (dayjs.isDayjs(date)) return date.unix();
  return dayjs(date).unix();
}

/**
 * Format a date for display in client timezone
 * @param date - Unix timestamp (seconds), ISO string, or Dayjs object
 * @param format - Format string
 * @param fromTimezone - Source timezone (defaults to server timezone)
 * @returns Formatted date string
 */
export function _clientTimeFormat(
  date: number | string | Date | Dayjs,
  format: string,
  fromTimezone: string = getServerTimezone()
): string {
  return _clientTime(date, fromTimezone).format(format);
}

/**
 * Get current date/time in client timezone
 * @returns Dayjs object in client timezone
 */
export function _clientNow(): Dayjs {
  return dayjs().tz(getClientTimezone());
}

/**
 * Get current date/time in server timezone
 * @returns Dayjs object in server timezone
 */
export function _serverNow(): Dayjs {
  return dayjs().tz(getServerTimezone());
}

/**
 * Parse a Unix timestamp and return it in client timezone
 * @param timestamp - Unix timestamp in seconds
 * @returns Dayjs object in client timezone
 */
export function _unixToClientTime(timestamp: number): Dayjs {
  return dayjs.unix(timestamp).tz(getClientTimezone());
}

/**
 * Parse a date string in server timezone and return it in client timezone
 * @param dateString - Date string from server
 * @param format - Optional format to parse with
 * @returns Dayjs object in client timezone
 */
export function _serverTimeToClientTime(dateString: string, format?: string): Dayjs {
  const serverTz = getServerTimezone();
  const clientTz = getClientTimezone();
  if (format) return dayjs.tz(dateString, format, serverTz).tz(clientTz);
  return dayjs.tz(dateString, serverTz).tz(clientTz);
}
